import React, { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {Order, OrderWithActiveDelivery, DeliveryFormData, Address} from '../types';
import CreateDeliveryModal from '../components/CreateDeliveryModal';
import ConfirmModal from '../components/ConfirmModel';
import StatusBadge from '../components/StatusBadge';
import { supabase } from '../clients/supabaseClient';
import { useCustomers } from '../hooks/useCustomers';
import {
  assignDriverToOrderInDb,
  unassignDriverFromOrderInDb,
  fetchTotalDeliveryCountFromDb,
  fetchOrdersWithActiveDeliveriesFromDb,
} from '../services/deliveryService';
import {
  fetchHomeOrderCountsFromDb,
  fetchOrdersNeedingDriverFromDb,
} from '../services/orderService';
import { ORDER_STATUS } from '../constants/orderStatuses';
import OrderTypeBadge from "../components/OrderTypeBadge.tsx";
import ActiveDeliveriesTable from "../components/ActiveDeliveriesTable.tsx";

interface HomeProps { title?: string; }

interface Card {
  title: string;
  value: number | string;
  link: string;
  state?: Record<string, unknown>;
}

const fmt        = (n: number) => new Intl.NumberFormat('en-ZA').format(n);
const formatDate = (d?: string | null) => d ? new Date(d).toLocaleString() : '–';

const Home: FC<HomeProps> = () => {
  const { fetchAddressesForCustomer } = useCustomers();

  const [openOrderCount, setOpenOrderCount]            = useState(0);
  const [activeDeliveryCount, setActiveDeliveryCount]        = useState(0);
  const [totalDeliveryCount,         setTotalDeliveryCount]         = useState(0);
  const [packerAssignedCount,        setPackerAssignedCount]        = useState(0);
  const [awaitingPackingCount,       setAwaitingPackingCount]       = useState(0);
  const [ordersNeedingDriver,        setOrdersNeedingDriver]        = useState<Order[] | null>(null);
  const [ordersWithActiveDeliveries, setOrdersWithActiveDeliveries] = useState<OrderWithActiveDelivery[] | null>(null);
  const [pendingAcceptance,          setPendingAcceptance]          = useState<OrderWithActiveDelivery[] | null>(null);
  const [isUnassignModalOpen,        setIsUnassignModalOpen]        = useState(false);
  const [orderToUnassign,            setOrderToUnassign]            = useState<string | number | null>(null);
  const [isCreateDeliveryModalOpen,  setIsCreateDeliveryModalOpen]  = useState(false);
  const [selectedOrder,              setSelectedOrder]              = useState<Order | null>(null);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading,                    setLoading]                    = useState(false);
  const [newDelivery,                setNewDelivery]                = useState<DeliveryFormData>({
    destination_id: '',
    driver_id:      '',
  });

  const refreshTimeoutRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef    = useRef(false);
  const pendingRefreshRef  = useRef(false);
  const refreshHomeDataRef = useRef<(() => Promise<void>) | null>(null);

  const navigate = useNavigate();

  const cards: Card[] = [
    { title: 'Open Orders', value: openOrderCount, link: '/orders' },
    { title: 'Active Deliveries', value: activeDeliveryCount, link: '/activedeliveries' },
    { title: 'Deliveries', value: totalDeliveryCount,  link: '/deliveries' },
    {
      title: 'Packing (Awaiting / Assigned)',
      value: `${awaitingPackingCount} / ${packerAssignedCount}`,
      link:  '/orders',
      state: { statuses: [ORDER_STATUS.AWAITING_PACKING, ORDER_STATUS.PACKER_ASSIGNED] },
    },
  ];

  const refreshHomeData = useCallback(async () => {
    if (isRefreshingRef.current) { pendingRefreshRef.current = true; return; }
    isRefreshingRef.current = true;
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session?.access_token) return;

      const [td, ond, owad, orderCounts] = await Promise.all([
        fetchTotalDeliveryCountFromDb(),
        fetchOrdersNeedingDriverFromDb(),
        fetchOrdersWithActiveDeliveriesFromDb(),
        fetchHomeOrderCountsFromDb(),
      ]);

      setOpenOrderCount(orderCounts.open);
      setAwaitingPackingCount(orderCounts.awaitingPacking);
      setPackerAssignedCount(orderCounts.packerAssigned);
      setTotalDeliveryCount(td);
      setOrdersNeedingDriver(ond);

      const ecomOrChat = owad.filter(o => o.type === 'ecom' || o.type === 'chat');
      const pending = ecomOrChat.filter(o => (o.delivery_status ?? '').trim().toUpperCase() === ORDER_STATUS.DRIVER_ASSIGNED);
      const active  = ecomOrChat.filter(o => (o.delivery_status ?? '').trim().toUpperCase() === ORDER_STATUS.ON_ROUTE);

      setPendingAcceptance(pending);
      setOrdersWithActiveDeliveries(active);
      setActiveDeliveryCount(active.length);
    } catch {
      // silently fail — realtime will retry
    } finally {
      isRefreshingRef.current = false;
      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        window.setTimeout(() => { void refreshHomeDataRef.current?.(); }, 1000);
      }
    }
  }, []);

  useEffect(() => { refreshHomeDataRef.current = refreshHomeData; }, [refreshHomeData]);
  useEffect(() => { void refreshHomeDataRef.current?.(); }, []);

  useEffect(() => {
    const scheduleRefresh = () => {
      if (document.hidden) return;
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = setTimeout(() => { void refreshHomeDataRef.current?.(); }, 750);
    };
    const channel = supabase
        .channel('home-realtime')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' },    scheduleRefresh)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, scheduleRefresh)
        .subscribe();
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      void supabase.removeChannel(channel);
    };
  }, []);

  const goToOrder    = (id: string | number) => navigate(`/orders/${id}`);
  const goToNewOrder = () => navigate('/orders', { state: { openCreate: true } });

  const openAssignFor = async (order: Order) => {
    setSelectedOrder(order);
    const initialAddressId = order.delivery_address_id || order.customer?.delivery_address || null;
    setNewDelivery({ destination_id: initialAddressId, driver_id: null });
    if (order.customer_id) {
      const fetched = await fetchAddressesForCustomer(String(order.customer_id));
      setAddresses(fetched);
    }
    setIsCreateDeliveryModalOpen(true);
  };

  const handleDeliverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrder || !newDelivery.destination_id || !newDelivery.driver_id) return;
    try {
      setLoading(true);
      const success = await assignDriverToOrderInDb(
          String(selectedOrder.id),
          selectedOrder.customer_id,
          newDelivery.destination_id,
          newDelivery.driver_id,
      );
      if (success) {
        setIsCreateDeliveryModalOpen(false);
        setSelectedOrder(null);
        await refreshHomeData();
      }
    } finally {
      setLoading(false);
    }
  };

  const triggerUnassignModal = (e: React.MouseEvent, orderId: string | number) => {
    e.stopPropagation();
    setOrderToUnassign(orderId);
    setIsUnassignModalOpen(true);
  };

  const handleConfirmUnassign = async () => {
    if (!orderToUnassign) return;
    try {
      setLoading(true);
      await unassignDriverFromOrderInDb(orderToUnassign);
      setIsUnassignModalOpen(false);
      setOrderToUnassign(null);
      await refreshHomeData();
    } catch {
      alert('Failed to unassign driver.');
    } finally {
      setLoading(false);
    }
  };

  const TH = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500';

  return (
      <div className="space-y-8">
        <header>
          <h1 className="text-2xl font-semibold text-stone-900">Overview</h1>
        </header>

        {/* Stat cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => {
            const isOpenOrders = c.title === 'Open Orders';
            return (
                <div key={i} className="group rounded-2xl bg-white border border-stone-200 shadow-sm p-4 transition hover:shadow-md hover:-translate-y-[1px]">
                  <div className="text-sm text-stone-500">{c.title}</div>
                  <div className="mt-2 text-2xl font-medium tracking-tight text-stone-900">
                    {typeof c.value === 'number' ? fmt(c.value) : c.value}
                  </div>
                  <div className="mt-3 h-px bg-stone-200" />
                  <div className="mt-3 text-xs text-stone-600 flex items-center gap-2">
                    <Link to={c.link} state={c.state}
                          className="inline-flex items-center rounded-full border border-stone-300 px-2 py-0.5 hover:bg-stone-100 transition-colors">
                      View
                    </Link>
                    {isOpenOrders && (
                        <button onClick={goToNewOrder}
                                className="inline-flex items-center rounded-full px-2 py-0.5 bg-[#ee2b74] text-white hover:bg-[#d4245f] transition-colors">
                          New
                        </button>
                    )}
                    <span className="ml-auto opacity-0 group-hover:opacity-100 transition">→</span>
                  </div>
                </div>
            );
          })}
        </section>

        {/* Assign a driver */}
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-200">
            <h2 className="font-semibold text-stone-900">Assign a Driver to an Order</h2>
          </div>
          {ordersNeedingDriver === null ? (
              <div className="p-4 text-sm text-stone-400">Loading…</div>
          ) : ordersNeedingDriver.length === 0 ? (
              <div className="p-4 text-sm text-stone-500">No pending assignments.</div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className={TH}>Order Ref</th>
                    <th className={TH}>Customer</th>
                    <th className={TH}>Type</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Created</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Action</th>
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {ordersNeedingDriver.map(order => (
                      <tr key={order.id} onClick={() => goToOrder(order.id)}
                          className="cursor-pointer hover:bg-stone-50 transition">
                        <td className="px-4 py-3 font-medium text-stone-900">{order.reference}</td>
                        <td className="px-4 py-3 text-stone-700">
                          {order.customer?.first_name} {order.customer?.last_name}
                        </td>
                        <td className="px-4 py-3">
                          <OrderTypeBadge type={order.type} />
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={order.status ?? ''} /></td>
                        <td className="px-4 py-3 text-stone-500 text-xs">{formatDate(order.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={e => { e.stopPropagation(); void openAssignFor(order); }}
                                  className="px-3 py-1.5 bg-stone-900 text-white text-xs rounded-full hover:bg-stone-700 transition">
                            Assign
                          </button>
                        </td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}
        </section>

        {/* Pending driver acceptance */}
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-200">
            <h2 className="font-semibold text-stone-900">Pending Driver Acceptance</h2>
          </div>
          {pendingAcceptance === null ? (
              <div className="p-4 text-sm text-stone-400">Loading…</div>
          ) : pendingAcceptance.length === 0 ? (
              <div className="p-4 text-sm text-stone-500">No orders waiting for driver acceptance.</div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className={TH}>Order Ref</th>
                    <th className={TH}>Customer</th>
                    <th className={TH}>Assigned Driver</th>
                    <th className={TH}>Time Elapsed</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Action</th>
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {pendingAcceptance.map(order => (
                      <tr key={order.id} onClick={() => goToOrder(order.id)}
                          className="cursor-pointer hover:bg-stone-50 transition">
                        <td className="px-4 py-3 font-medium text-stone-900">{order.order_reference}</td>
                        <td className="px-4 py-3 text-stone-700">{order.customer}</td>
                        <td className="px-4 py-3">
                                            <span className="border border-stone-300 px-2 py-0.5 text-xs text-stone-700 rounded-full inline-flex items-center gap-1.5">
                                                <span className="h-2 w-2 rounded-full bg-[#f5ab40] animate-pulse" />
                                              {order.driver_name}
                                            </span>
                        </td>
                        <td className="px-4 py-3 text-stone-500 text-xs">{formatDate(order.assigned_date)}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={e => triggerUnassignModal(e, order.id)} disabled={loading}
                                  className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full hover:bg-red-100 transition disabled:opacity-50">
                            {loading ? '…' : 'Unassign'}
                          </button>
                        </td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}
        </section>

        {/* Orders out for delivery */}
        <ActiveDeliveriesTable orders={ordersWithActiveDeliveries} />

        <ConfirmModal
            isOpen={isUnassignModalOpen}
            title="Unassign Driver"
            message="Are you sure you want to remove this driver from the order?"
            onConfirm={handleConfirmUnassign}
            onCancel={() => { setIsUnassignModalOpen(false); setOrderToUnassign(null); }}
            loading={loading}
        >
          <div className="flex items-center gap-3 text-stone-600 rounded-lg">
            <svg className="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-xs font-medium">
              The order will be moved back to the 'Assign Driver' list, and the driver will no longer see it in their app.
            </p>
          </div>
        </ConfirmModal>

        {isCreateDeliveryModalOpen && (
            <CreateDeliveryModal
                isOpen={isCreateDeliveryModalOpen}
                onClose={() => setIsCreateDeliveryModalOpen(false)}
                newDelivery={newDelivery}
                onDeliveryChange={e => setNewDelivery(p => ({ ...p, destination_id: e.target.value  }))}
                onDriverChange={e => setNewDelivery(p => ({ ...p, driver_id: e.target.value }))}
                onSubmit={handleDeliverySubmit}
                loading={loading}
                customerAddresses={addresses}
                selectedOrder={selectedOrder}
            />
        )}
      </div>
  );
};

export default Home;